import { PlanId } from 'src/app/pages/new-billing/types/api.types';
import { LegacyPricingPlans } from 'src/app/pages/new-billing/types/plan.types';
import { getPlanTierFromPlanId, planIdToPricingPlan, planTierRank, pricingPlanToPlanId } from 'src/app/pages/new-billing/utils/plan-maps';
import { CompanySettings, WebAppTrackingMode } from 'src/models';

export function resolvePricingPlan(planId: string): string {
  return planIdToPricingPlan[planId as PlanId] ?? planId;
}

export function resolvePlanIdFromPricingPlan(pricingPlan: string | undefined): string | null {
  if (!pricingPlan) {
    return null;
  }

  const legacyPlanId = pricingPlanToPlanId[pricingPlan as LegacyPricingPlans];
  if (legacyPlanId) {
    return legacyPlanId;
  }

  if (getPlanTierFromPlanId(pricingPlan) > 0) {
    return pricingPlan;
  }

  return null;
}

export function shouldWorkScheduleFeatureBeEnabled(pricingPlan: string | undefined): boolean {
  if (!pricingPlan || pricingPlan.includes('basic')) {
    return false;
  }

  const planId = resolvePlanIdFromPricingPlan(pricingPlan);
  if (!planId) {
    return !pricingPlan.includes('basic');
  }

  return !!getPlanDerivedCompanySettings(planId).workScheduleFeature;
}

export function getPlanDerivedCompanySettings(planId: string): Partial<CompanySettings> {
  const tier = getPlanTierFromPlanId(planId);
  const isBasic = tier === planTierRank.basic;
  const isPremium = tier >= planTierRank.premium;

  return {
    webAndAppTracking: isBasic ? WebAppTrackingMode.Off : WebAppTrackingMode.Extended,
    workScheduleFeature: !isBasic,
    trackConnectivity: isPremium,
  };
}

export type PlanSettingChangeContext = {
  /** True when the company is currently on Basic before applying `planId`. */
  wasBasic?: boolean;
};

/**
 * Company-setting changes to apply when a company moves to `planId`.
 *
 * Plan-gated features are normally only DISABLED — when the target plan drops
 * support for them. Features the plan still supports keep the user's current
 * value so intentional opt-outs survive (e.g. Premium -> Standard keeps a
 * manually-disabled schedules/web-tracking off).
 *
 * Exception: leaving Basic restores paid defaults for web & app tracking.
 * Basic force-disables tracking (not a user opt-out). Matching Production,
 * Basic -> Standard/Premium enables Extended so `/web-app-usage` is reachable
 * after upgrade instead of `/error/webAppUsage`. Schedules stay off until
 * setup/toggle (opt-in).
 */
export function getPlanSettingChangesToApply(
  planId: string,
  context: PlanSettingChangeContext = {},
): Partial<CompanySettings> {
  const tier = getPlanTierFromPlanId(planId);
  const isBasic = tier === planTierRank.basic;
  const isPremium = tier >= planTierRank.premium;

  const changes: Partial<CompanySettings> = {};

  if (isBasic) {
    changes.webAndAppTracking = WebAppTrackingMode.Off;
    changes.workScheduleFeature = false;
  } else if (context.wasBasic) {
    changes.webAndAppTracking = WebAppTrackingMode.Extended;
  }

  if (!isPremium) {
    changes.trackConnectivity = false;
  }

  return changes;
}

export function isBasicPlanId(planId: string): boolean {
  return getPlanTierFromPlanId(planId) === planTierRank.basic;
}
